import { Button, Col, DatePicker, Empty, Row, Select, Spin } from 'antd';
import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react'
import { Label } from '../../../../components/Label/Label';
import moment, { Moment } from 'moment';
import { DatasetRecord } from '../../Store/dataTypes';
import { DT0Format, DateFormat } from '../../../../generic/StaticVariable';
import { ChartBox } from '../ChartBox';
import { IGetBasisChartV2ApiPayload } from '../../../../typings/chart';
import { IDatasetBrief, IEnrichedMetadata } from '../../../../typings/metadata';
import { IBestDataset, IItemsValidation } from '../../../../typings/analytics';
import { MarkerItem } from '../../../../typings/home';
import Api from '../../../../api/Api';
import MultipleLineSeries from '../../../../components/Chart/MultipleLineChart';
import { DownloadCheckboxKey } from '../../../../generic/common';
import { StoreContext } from '../../../../store/store';
import styles from './Plot.module.scss'
import { LoadingOutlined } from '@ant-design/icons';

interface ITimeSeries {
  activePointMetadata?: DatasetRecord
  validInSituPoint?: boolean
  activePoint?: MarkerItem
  activeTab: number
  enrichedMetadata?: IEnrichedMetadata[]
  listParameters: IItemsValidation[],
  dateRange?: [moment.Moment, moment.Moment]
  tooltipName?: {
    insitu?: string,
    point?: string
  }
  activeCategoryKey: string
  setActiveCategory: Function
}

const TimeSeries: React.FC<ITimeSeries> = ({
  activeCategoryKey,
  setActiveCategory,
  activePointMetadata,
  validInSituPoint,
  activePoint,
  enrichedMetadata,
  tooltipName,
  listParameters,
  dateRange,
  activeTab
}) => {
  const [variable, setVariable] = useState('')
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [loading, setLoading] = useState(false)
  const [seriesDataPoint, setSeriesDataPoint] = useState<Array<{ Time: Date; Value: number }>>([])
  const [seriesDataInsitu, setSeriesDataInsitu] = useState<Array<{ Time: Date; Value: number }>>([])
  const { datasetBriefs } = useContext(StoreContext)

  useEffect(() => {
    if (enrichedMetadata && listParameters.length > 0 && activePointMetadata?.userGroupPermissions && validInSituPoint && dateRange) {
      const parameter = listParameters[0].name
      const maxStartDate = moment(dateRange[1]).subtract(20, 'years');
      setVariable(parameter)

      if(moment(dateRange[0]).isBefore(maxStartDate)){
        setStartDate(maxStartDate)
      }else{
        setStartDate(moment(dateRange[0]))
      }
      setEndDate(moment(dateRange[1]))
      const values: IGetBasisChartV2ApiPayload = {
        datasetName: activePointMetadata.metadata.DatasetId,
        parameters: listParameters[0].details,
        startDate: startDate?.format(DT0Format) ?? moment(dateRange[0]).format(DT0Format),
        endDate: endDate?.format(DT0Format) ?? moment(dateRange[1]).format(DT0Format),
      };
      if(activeTab === 0 && seriesDataPoint.length === 0 && seriesDataInsitu.length === 0){
        onLoadTimeSeries(values, enrichedMetadata, parameter)
      }
    }

  }, [activeTab, enrichedMetadata, activePointMetadata?.metadata, activePointMetadata?.userGroupPermissions, activePoint?.coordinates, validInSituPoint, listParameters, dateRange])

  const onLoadTimeSeries = async (values: IGetBasisChartV2ApiPayload, enrichedMetadata: IEnrichedMetadata[], variable: string) => {
    const category = activePointMetadata?.bestDataset?.find(
      (bd: IBestDataset) => bd.id === values.datasetName
    );

    if (category !== undefined) {
      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === values.datasetName &&
          task.CoordinatesString === activePoint?.coordinates.join()
      );
      if (task) {
        const brief = datasetBriefs.find((brief: IDatasetBrief) => brief.Id === values.datasetName)
        const isFwe = brief?.IsFWE
        try {
          setLoading(true)
          const pointChart = await Api.chart.getBasicChartV2(
            task.OrderId,
            task.RequestId,
            isFwe ? brief.Id : category.label,
            values.parameters,
            values.startDate,
            values.endDate,
            undefined,
            isFwe
          );
          if (pointChart[0].Data.Items) {
            setSeriesDataPoint(
              pointChart[0].Data.Items.map((ts: {
                Time: string;
                [key: string]: string
              }) => {
                return {
                  Time: moment(ts.Time, 'YYYY-MM-DD HH:mm:ss').toDate(),
                  Value: parseFloat(ts[values.parameters?.split(' ').join('_') || ''])
                };
              })
            )
          }

          const insituChart = await Api.chart.getTimeseriesInsitu(
            tooltipName?.insitu ?? '',
            task.OrderId,
            variable,
            values.startDate,
            values.endDate
          )

          if (insituChart?.length > 0) {
            setSeriesDataInsitu(
              insituChart.map((ts: {
                dateTime: string,
                value: string
              }) => {
                return {
                  Time: moment(ts.dateTime, 'YYYY-MM-DD HH:mm:ss').toDate(),
                  Value: parseFloat(ts.value) === -9999 ? undefined : parseFloat(ts.value)
                }
              })
            )

          }
        } finally {
          setLoading(false)
        }
      }
    }
  }

  const onChangeStartDate = (value: any) => {
    if (value) {
      setStartDate(value);
    }
  };

  const onChangeEndDate = (value: any) => {
    if (value) {
      setEndDate(value);
    }
  };

  const onSelectChange = (value: string) => {
    setVariable(value)
  }

  const onChangeParameter = (value: string) => {
    setActiveCategory(value)
  }

  const handleUpdate = () => {
    if (enrichedMetadata && activePointMetadata?.metadata && startDate && endDate) {
      const findParameters = listParameters.find(lp => lp.name === variable)?.details ?? ''
      const values: IGetBasisChartV2ApiPayload = {
        datasetName: activePointMetadata?.metadata?.DatasetId,
        parameters: findParameters,
        startDate: startDate?.format(DT0Format),
        endDate: endDate?.format(DT0Format),
      };
      onLoadTimeSeries(values, enrichedMetadata, variable)
    }
  }

  const actionRows = (
    <Fragment>
      <Row className={!validInSituPoint ? styles.unactiveActionRow : styles.activeActionRow}>
        <Col span={6}>
          <Label label="Parameters" >
            <Select
              value={activeCategoryKey ? activeCategoryKey  as string : DownloadCheckboxKey.SW  as string}
              onChange={onChangeParameter}
              size="small"
              className={styles.field}
              disabled={!validInSituPoint || loading}
            >
              {Object.entries(DownloadCheckboxKey).map(([key, val]) => (
                <Select.Option key={key} value={val}>
                  {val}
                </Select.Option>
              ))}
            </Select>
          </Label>
        </Col>
        <Col span={6}>
          <Label label="Variables" >
            <Select
              value={variable}
              onChange={onSelectChange}
              size="small"
              disabled={!validInSituPoint || loading}
              className={styles.field}
            >
              {listParameters
                .map((item) => {
                  return (
                    <Select.Option key={item.id} value={item.name}>
                      {item.name}
                    </Select.Option>
                  );
                })}
            </Select>
          </Label>
        </Col>
        <Col span={6}>
          <Label label="Start Date">
            <DatePicker
              size="small"
              className={styles.field}
              value={moment(startDate)}
              onChange={onChangeStartDate}
              format={DateFormat}
              disabled={!validInSituPoint || loading}
            />
          </Label>
        </Col>
        <Col span={6}>
          <Label label="End Date">
            <DatePicker
              size="small"
              className={styles.field}
              value={moment(endDate)}
              onChange={onChangeEndDate}
              format={DateFormat}
              disabled={!validInSituPoint || loading}
            />
          </Label>
        </Col>
      </Row>
      <Row className={!validInSituPoint ? styles.unactiveActionRow : styles.activeActionRow}>
        <Col span={3} className={styles.buttonUpdateWrapper}>
          <Button
            className={styles.button}
            type='primary'
            onClick={handleUpdate}
            disabled={!validInSituPoint || loading}
          >
            Update
          </Button>
        </Col>
      </Row>
    </Fragment>
  );

  return (
    <ChartBox
      actionRow={actionRows}
      key={'Time Series Plot'}
      title={'Time Series Plot'}
      height={320}
      plot={
        <>
          {loading && <Spin indicator={<LoadingOutlined style={{ fontSize: 50 }} spin/>} />}
          {!loading && validInSituPoint && (
            <MultipleLineSeries
              data={[seriesDataInsitu, seriesDataPoint]}
              names={[tooltipName?.insitu || 'Insitu', tooltipName?.point || 'Point']}
              colors={['red', 'blue']}
            />
            )}
            {!loading && seriesDataInsitu.length === 0 && seriesDataPoint.length === 0 && (
              <Empty />
            ) }
        </>
      }
      dataset=''
    />
  )
}


export default TimeSeries
